import {useParams} from "react-router-dom";
import {useConfiguration} from "@/features/configuration";
import {useOrderContext} from "../context";

export type FlowStep = "pickup" | "dropoff" | "overview" | "confirmation";

/**
 * Hook for checking the validity of the current flow state.
 * Provides centralized validation logic for determining which steps are accessible.
 *
 * This ensures consistent validation across route guards and UI logic.
 */
export function useFlowState() {
    const {data: configuration} = useConfiguration();
    const {orderDraft} = useOrderContext();
    const {orderId} = useParams();

    const hasPickup = !!orderDraft.pickup;
    const hasDropoff = !!orderDraft.dropoff;
    const hasContactInfo = !!orderDraft.name && !!orderDraft.phoneNumber;

    const hasFixedPickup = !!configuration?.fixedPickup;
    const hasFixedDestination = !!configuration?.fixedDestination;

    const hasOrderId = !!orderId;

    /**
     * Check if a specific step is accessible based on current form state.
     */
    const canAccessStep = (step: FlowStep): boolean => {
        switch (step) {
            case "pickup":
                return !hasFixedPickup;

            case "dropoff":
                return !hasFixedDestination;

            case "overview":
                return hasPickup && hasDropoff;

            case "confirmation":
                return hasOrderId;

            default:
                return false;
        }
    };

    /**
     * Get the appropriate redirect path for a step that's not accessible.
     * Returns the step the user should be redirected to.
     */
    const getRedirectPath = (attemptedStep: FlowStep): string => {
        if (attemptedStep === "overview") {
            // Missing locations -> go to pickup
            if (!hasPickup) return "pickup";
            if (!hasDropoff) return "dropoff";
        }

        // Default: start from beginning
        return "pickup";
    };

    /**
     * Get the next logical step based on current form state.
     */
    const getNextStep = (): FlowStep => {
        if (!hasPickup) return "pickup";
        if (!hasDropoff) return "dropoff";
        return "overview";
    };

    return {
        // State checks
        hasPickup,
        hasDropoff,
        hasContactInfo,

        // Validation functions
        canAccessStep,
        getRedirectPath,
        getNextStep,

        // Summary
        isReadyForOverview: hasPickup && hasDropoff,
        isFlowComplete: hasPickup && hasDropoff && hasContactInfo,
    };
}
